import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { errorAlertMessage } from '../../common/views/ErrorAlert/errorAlertMessage';
import { useDeleteSongPublishMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { servicesPanelMessages as m } from './servicesPanelMessages';

interface CancelUploadDialogProps {
  accountId: number;
  /** The waiting upload to cancel; null: the question is closed. */
  publish: SerializedSongPublish | null;
  songTitle: string;
  onClose: () => void;
}

/** Cancel of a waiting upload (design d3: "Cancel asks one time, then deletes the waiting publish"). */
const CancelUploadDialogComponent: React.FC<CancelUploadDialogProps> = ({ accountId, publish, songTitle, onClose }) => {
  const intl = useIntl();
  const [remove, state] = useDeleteSongPublishMutation();
  const confirm = useCallback(() => {
    if (publish === null) {
      return;
    }
    remove({ accountId, catalogItemUuid: publish.catalogItemUuid, publishUuid: publish.uuid })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          onClose();
        }
      })
      .catch(console.error);
  }, [accountId, onClose, publish, remove]);
  return (
    <ConfirmDialog
      isOpen={publish !== null}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={faXmark} />}
      title={intl.formatMessage(m.cancelUploadTitle)}
      description={intl.formatMessage(m.cancelUploadBody, { title: songTitle })}
      cancelLabel={intl.formatMessage(m.cancelUploadKeep)}
      confirmLabel={intl.formatMessage(m.cancelUploadConfirm)}
      isBusy={state.isLoading}
      error={state.error === undefined ? undefined : errorAlertMessage(intl, state.error)}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
};

export const CancelUploadDialog = TypedMemo(CancelUploadDialogComponent);
